import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Shield } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const NewAdminDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/admins", form);
      toast.success("Admin creato");
      setForm({ email: "", password: "", name: "" });
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-black/80 backdrop-blur-md" />
      <div data-testid="new-admin-dialog" className="relative bg-zinc-950 border-4 border-yellow-400 max-w-md w-full">
        <div className="flex items-center justify-between p-5 border-b-2 border-zinc-800">
          <h3 className="font-heading text-2xl uppercase text-white">Nuovo Admin</h3>
          <button onClick={onClose} className="p-2 text-white hover:bg-yellow-400 hover:text-black border-2 border-zinc-800">
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Nome</label>
            <input data-testid="new-admin-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none" />
          </div>
          <div>
            <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Email</label>
            <input data-testid="new-admin-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none font-mono" />
          </div>
          <div>
            <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Password</label>
            <input data-testid="new-admin-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none font-mono" />
            <div className="text-zinc-500 text-xs mt-1">Min 6 caratteri</div>
          </div>
          <button type="submit" data-testid="save-new-admin" disabled={saving}
            className="w-full bg-yellow-400 text-black font-heading uppercase py-3 hover:bg-white transition disabled:opacity-40">
            {saving ? "Creazione..." : "Crea Admin"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default function AdminsPage() {
  const { admin: current } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/admins");
      setAdmins(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const del = async (a) => {
    if (!window.confirm(`Eliminare admin "${a.email}"?`)) return;
    try {
      await api.delete(`/admin/admins/${a.id}`);
      toast.success("Admin eliminato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  return (
    <div data-testid="admin-admins" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="font-jp text-yellow-400 text-xs tracking-widest mb-2">管理者</div>
          <h1 className="font-heading text-5xl uppercase text-white leading-none">Admin</h1>
          <p className="text-zinc-400 mt-2">Gestisci chi ha accesso al pannello.</p>
        </div>
        <button data-testid="new-admin-button" onClick={() => setDialogOpen(true)}
          className="bg-yellow-400 text-black font-heading uppercase px-6 py-3 hover:bg-white transition flex items-center gap-2">
          <Plus className="w-5 h-5" strokeWidth={3} /> Nuovo Admin
        </button>
      </div>

      <div className="bg-zinc-900 border-2 border-zinc-800 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-950 border-b-2 border-zinc-800">
            <tr className="text-left">
              {["Nome", "Email", "Ruolo", "Creato il", "Azioni"].map((h) => (
                <th key={h} className="font-heading uppercase text-yellow-400 text-xs tracking-widest p-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-8 text-center text-zinc-500 uppercase">Caricamento...</td></tr>}
            {admins.map((a) => {
              const isSelf = a.id === current.id;
              const isSuper = a.role === "super_admin";
              return (
                <tr key={a.id} data-testid={`admin-row-${a.id}`} className="border-b border-zinc-800 hover:bg-zinc-950/50">
                  <td className="p-3 text-white font-bold">{a.name} {isSelf && <span className="text-yellow-400 text-xs ml-2">(tu)</span>}</td>
                  <td className="p-3 text-zinc-300 font-mono text-sm">{a.email}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-heading uppercase tracking-widest ${isSuper ? "bg-yellow-400 text-black" : "bg-zinc-800 text-zinc-300"}`}>
                      {isSuper && <Shield className="w-3 h-3" strokeWidth={3} />} {a.role}
                    </span>
                  </td>
                  <td className="p-3 text-zinc-400 text-sm">{new Date(a.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="p-3">
                    <button data-testid={`delete-admin-${a.id}`} onClick={() => del(a)} disabled={isSelf || isSuper}
                      className="bg-zinc-800 text-red-400 p-2 hover:bg-red-600 hover:text-white transition disabled:opacity-30 disabled:cursor-not-allowed">
                      <Trash2 className="w-4 h-4" strokeWidth={3} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <NewAdminDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onCreated={load} />
    </div>
  );
}
